import { HTMLSelect } from "@blueprintjs/core";
import { Button } from "@focus/focusUI";

interface OperatorConfigHeaderProps {
  isLoading: boolean;
  hasChanges: boolean;
  hasErrors: boolean;
  title?: string;
  operators: string[];
  selectedOperator: string | null;
  showActionButtons: boolean;
  onOperatorChange: (operator: string) => void;
  handleSave: () => void;
  handleExport: () => void;
  handleReset: () => void;
}

export const OperatorConfigHeader = ({
  isLoading,
  hasChanges,
  hasErrors,
  title,
  operators,
  selectedOperator,
  showActionButtons,
  onOperatorChange,
  handleSave,
  handleExport,
  handleReset,
}: OperatorConfigHeaderProps) => (
  <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4">
    <div className="min-w-0 flex-1">
      <div className="text-xs font-semibold uppercase text-slate-500">
        System configuration
      </div>
      <h2 className="mt-1 text-lg font-bold text-slate-950">
        {title || "Operator Config"}
      </h2>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase">
        <span
          className={`rounded-full px-2.5 py-1 ring-1 ring-inset ${
            isLoading
              ? "bg-slate-100 text-slate-600 ring-slate-200"
              : hasErrors
                ? "bg-red-50 text-red-700 ring-red-200"
                : hasChanges
                  ? "bg-amber-50 text-amber-700 ring-amber-200"
                  : "bg-emerald-50 text-emerald-700 ring-emerald-200"
          }`}
        >
          {isLoading
            ? "Loading"
            : hasErrors
              ? "Has errors"
              : hasChanges
                ? "Unsaved changes"
                : "Up to date"}
        </span>
      </div>
    </div>

    <div className="focusoc-form-shell flex min-w-[min(100%,460px)] flex-wrap items-end justify-end gap-3">
      <div className="operator-config-header-actions flex h-[38px] w-[116px] items-center justify-end">
        {!isLoading && showActionButtons && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              className="focusoc-icon-button"
              disabled={!hasChanges}
              icon="reset"
              onClick={handleReset}
              title="Reset changes"
            />
            <Button
              className="focusoc-icon-button"
              icon="download"
              onClick={handleExport}
              title="Export JSON"
            />
            <Button
              className={`focusoc-icon-button ${
                hasErrors
                  ? "focusoc-icon-button-danger"
                  : hasChanges
                    ? "focusoc-icon-button-success"
                    : ""
              }`}
              disabled={!hasChanges || hasErrors}
              icon="floppy-disk"
              onClick={handleSave}
              title="Save configuration"
            />
          </div>
        )}
      </div>

      <label className="flex min-w-[220px] flex-1 flex-col gap-1.5">
        <span className="text-xs font-semibold uppercase text-slate-500">
          Operator
        </span>
        <HTMLSelect
          fill
          onChange={(e) => onOperatorChange(e.target.value)}
          options={[
            { label: "- Select operator -", value: "", disabled: true },
            ...operators.map((operator) => ({
              label: operator,
              value: operator,
            })),
          ]}
          value={selectedOperator ?? ""}
        />
      </label>
    </div>
  </header>
);
